import { useEffect, useState } from "react";
import { LoggedInLayoutSelector } from "../../Layouts/LoggedInLayoutSelector";
import { addTutorSubject, getAllTutorProfiles, removeTutorSubject } from "../../firestore/Functions/tutorProfileFunctions";
import { useUtility } from "../../contexts/auth/UtilityContext";
import { TutorProfileView, TutorSubject } from "../../firestore/Types/TutorProfileSchema";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SubjectTag } from "../../components/SubjectTag";
import { CalendarDaysOfWeekSimpleAvailabilty } from "../../components/Calendar/CalendarDaysOfWeekSimpleAvailability";
import { faSchool, faUser } from "@fortawesome/free-solid-svg-icons";
import { VerifyAdmin } from "./VerifyAdmin";

export function AdminTutorsView() {

    const [allTutors, setAllTutors] = useState<TutorProfileView[]>([]);

    const [selectedTutor, setSelectedTutor] = useState<TutorProfileView | null>(null);

    const [searchValue, setSearchValue] = useState<string>("");

    const [selectedSubject, setSelectedSubject] = useState<string>("");

    const [selectedMark, setSelectedMark] = useState<number>(0);

    const [isAddingSubject, setIsAddingSubject] = useState<boolean>(false);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const { subjects, schools, setNewToast } = useUtility();

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    }

    const handleSelectTutor = (tutor: TutorProfileView) => {
        setSelectedTutor(tutor);
    }

    const handleDeselectTutor = () => {
        setSelectedTutor(null);
    }

    const handleAddSubject = async () => {
        if (selectedTutor === null) {
            return;
        }
        if (selectedSubject === "") {
            setNewToast({ title: "Error", message: "Please select a subject", toastType: "error" });
            return;
        }
        setIsSaving(true);
        const newTutorSubject: TutorSubject = {
            SubjectID: selectedSubject,
            Mark: selectedMark
        }
        await addTutorSubject(newTutorSubject, selectedTutor?.Id ?? "").catch((error) => setNewToast({ title: "Error", message: error.message, toastType: "error" }));
        const newTutors = await getAllTutorProfiles();
        setAllTutors(newTutors);
        setSelectedTutor(newTutors.find(tutor => tutor.Id === selectedTutor?.Id) ?? null);
        setIsSaving(false);
    }

    const handleDeleteSubject = async (subject: TutorSubject) => {
        setIsSaving(true);
        await removeTutorSubject(subject, selectedTutor?.Id ?? "").catch((error) => setNewToast({ title: "Error", message: error.message, toastType: "error" }));
        const newTutors = await getAllTutorProfiles();
        setAllTutors(newTutors);
        setSelectedTutor(newTutors.find(tutor => tutor.Id === selectedTutor?.Id) ?? null);
        setIsSaving(false);
    }

    useEffect(() => {
        getAllTutorProfiles().then((tutors) => {
            setAllTutors(tutors);
        });
    }, []);
    
    return (
        <LoggedInLayoutSelector>
            <VerifyAdmin />
            <div className="main-bg min-h-[90vh]">
            {isSaving ? 
            <div className="flex flex-col justify-center items-center">
                <div className="flex flex-col gap-2">
                    <div>Saving...</div>
                </div>
            </div> : <>
            {selectedTutor === null ? <div className='flex flex-col justify-center items-center'>
                <div className="w-[80%]">
                    <input type="text" placeholder="Search" className="w-full border-2 border-gray-300 rounded-lg p-2" onChange={handleSearch} />
                </div>
                <div className="relative overflow-auto w-[80%]">
                    <ul>
                        {allTutors.filter(tutor => tutor.Name.toLowerCase().includes(searchValue.toLowerCase())).map(( tutor, index ) => {return TutorProfileRow({tutor: tutor, key: index})})}
                    </ul>
                </div>
            </div> : 
            <div className='flex flex-col justify-center items-center'>
                <div className="w-[80%]">
                    <button className="primary-button" onClick={handleDeselectTutor}>Back</button>
                </div>
                <div className="flex flex-col bg-white rounded-lg border-solid border-2 m-2 p-2 border-gray-300 w-[80%]">
                    <div className="flex flex-row">
                        <div className="flex flex-row w-[200px]">
                            <div className="flex flex-col m-2">
                                <div className="flex flex-row gap-2 mb-2">
                                    <FontAwesomeIcon icon={faUser} title="Name" color="#4b5563" width="20px"/>
                                    <div className="text-sm">{selectedTutor.Name}</div>
                                </div>
                                <div className="flex flex-row gap-2 mb-2">
                                    <FontAwesomeIcon icon={faSchool} title="School" color="#4b5563" width="20px" />
                                    <div className="text-sm">{schools.find(school => school.Id === selectedTutor.SchoolId)?.Name || "Unknown School"}</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col grow w-[200px]">
                            <div className="flex flex-col gap-1 grow mb-4">
                                {selectedTutor.Subjects.map((tutorSubject, index) => {
                                    const subject = subjects.find(subject => subject.Id === tutorSubject.SubjectID)?.SubjectName || "Unknown Subject";
                                    return <div className="flex w-full gap-2 m-2" key={index}><div className="grow"><SubjectTag subject={subject}></SubjectTag></div><button className="primary-button" onClick={() => handleDeleteSubject(tutorSubject)}>delete</button></div>;
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-2">
                        <div className="flex flex-row grow ">
                            <div className="flex flex-row m-2 margin-auto">
                                <CalendarDaysOfWeekSimpleAvailabilty Schedule={selectedTutor.Schedule} />
                            </div>
                        </div>
                    </div>
                </div>
                {isAddingSubject ? 
                <div className="w-[80%]" >
                    <div className="flex flex-col gap-2 m-2">
                        <select className="border-2 border-gray-300 rounded-lg p-2" onChange={(e) => setSelectedSubject(e.target.value)}>
                            <option disabled selected value=""> -- select an option -- </option>
                            {subjects.map((subject, index) => {
                                return <option key={index} value={subject.Id}>{subject.SubjectName}</option>
                            })}
                        </select>
                        <input type="number" min={0} max={100} placeholder="Enter mark (optional)" className="border-2 border-gray-300 rounded-lg p-2" onChange={(e) => setSelectedMark(Number(e.target.value))} />
                    </div>
                    <div className="flex flex-row gap-2">
                        <button className="primary-button" onClick={() => setIsAddingSubject(false)}>Cancel</button>
                        <button className="primary-button" onClick={() => handleAddSubject()}>Add</button>
                    </div>
                </div>
                : <div className="w-[80%]">
                    <button className="primary-button" onClick={() => {setIsAddingSubject(true)}}>Add Subject</button>
                </div>}
            </div>}</>}
            </div>
        </LoggedInLayoutSelector>
    );
    
    function TutorProfileRow({ tutor, key }: { tutor: TutorProfileView, key: number }) {
        return (
            <li key={key}>
                <div className="flex flex-col bg-white rounded-lg border-solid border-2 m-2 p-2 border-gray-300 w-[98%] hover:bg-gray-200" onClick={() => handleSelectTutor(tutor)}>
                    <div className="flex flex-row">
                        <div className="flex flex-row w-[200px]">
    
                            <div className="flex flex-col m-2">
                                {/* <div className="flex flex-row">
                                    <div className="text-sm grow">Rating: </div>
                                    <div className="text-xs rounded-lg bg-blue-500 text-white p-2">{tutor.tier}</div>
                                </div> */}
                                <div className="flex flex-row gap-2 mb-2">
                                    <FontAwesomeIcon icon={faUser} title="Name" color="#4b5563" width="20px"/>
                                    <div className="text-sm">{tutor.Name}</div>
                                </div>
                                <div className="flex flex-row gap-2 mb-2">
                                    <FontAwesomeIcon icon={faSchool} title="School" color="#4b5563" width="20px" />
                                    <div className="text-sm">{schools.find(school => school.Id === tutor.SchoolId)?.Name || "Unknown School"}</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col grow w-[200px]">
                            <div className="flex flex-col gap-1 grow mb-4">
                                {tutor.Subjects.map((tutorSubject, index) => {
                                    const subject = subjects.find(subject => subject.Id === tutorSubject.SubjectID)?.SubjectName || "Unknown Subject";
                                    return <SubjectTag key={index} subject={subject}></SubjectTag>;
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-2">
                        <div className="flex flex-row grow ">
                            <div className="flex flex-row m-2 margin-auto">
                                <CalendarDaysOfWeekSimpleAvailabilty Schedule={tutor.Schedule} />
                            </div>
                        </div>
                    </div>
                </div>
            </li>
        );
    }
}
